import {
  Box,
  Button,
  Card,
  CardHeader,
  styled,
  Table,
  TableBody,
  TableCell as TableCellOriginal,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { ReceiptPhoenix } from '@pflegenavi/frontend/api-nursing-home';
import { useGetPaymentProcessor } from '@pflegenavi/frontend/api-nursing-home';
import { useFormatting } from '@pflegenavi/frontend/localization';
import { generatePath, nhAppMainPages } from '@pflegenavi/frontend/routing';
import {
  Iconify,
  OneLineTypography,
  ReceiptStatusLabel,
  TableEmptyRows,
  TableNoData,
} from '@pflegenavi/web-components';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AllSetSvg from '../assets/all_set.svg';
import React from 'react';
const TableCell = styled(TableCellOriginal)`
  padding: 12px;
`;

// ----------------------------------------------------------------------

interface RecentReceiptsTableProps {
  data: ReceiptPhoenix[];
}

export const RecentReceiptsTable: FC<RecentReceiptsTableProps> = ({ data }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleViewAllReceipts = () => {
    navigate(nhAppMainPages.TRANSACTIONS_PENDING);
  };

  return (
    <Card
      sx={{
        flex: 1,
        minWidth: {
          xs: '100%',
          md: 560,
        },
      }}
      data-cy="latest-receipts"
    >
      <CardHeader
        title={t('dashboard.label.latestReceipts')}
        sx={{ mb: 3 }}
        slotProps={{
          title: {
            variant: 'h5',
            sx: { fontWeight: 600 },
          },
        }}
      />
      {/* TODO: extract style */}
      <TableContainer
        sx={{ maxWidth: '96%', marginLeft: 'auto', marginRight: 'auto' }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('dashboard.table.resident')}</TableCell>
              <TableCell>{t('dashboard.table.receiptType')}</TableCell>
              <TableCell align="right">{t('dashboard.table.amount')}</TableCell>
              <TableCell align="center">{t('common.status')}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {data?.length === 0 && (
              <TableNoData colSpan={4} isNotFound>
                <img
                  alt="empty content"
                  src={AllSetSvg}
                  height={170}
                  width={140}
                />
                <Typography
                  variant="inherit"
                  sx={{
                    fontWeight: '700',
                    textAlign: 'center',
                  }}
                >
                  {t('table.no-open-receipts-this-month')}
                </Typography>
              </TableNoData>
            )}
            {data.map((row, index) => {
              return <RecentReceiptsTableRows key={index} row={row} />;
            })}

            {data && data.length > 0 && (
              <TableEmptyRows
                height={48}
                emptyRows={tableMaxRows - data.length}
              />
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {data?.length > 0 && (
        <Box sx={{ p: 2, textAlign: 'right', mt: 'auto' }}>
          <Button
            size="small"
            color="inherit"
            endIcon={<Iconify icon={'eva:arrow-ios-forward-fill'} />}
            onClick={handleViewAllReceipts}
          >
            {t('dashboard.label.viewAll')}
          </Button>
        </Box>
      )}
    </Card>
  );
};

const RecentReceiptsTableRows: FC<{ row: ReceiptPhoenix }> = ({ row }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { fCurrencyCents } = useFormatting();
  const { t } = useTranslation();
  const paymentProcessor = useGetPaymentProcessor();

  const onClick = () => {
    navigate(
      generatePath(nhAppMainPages.TRANSACTIONS_EDIT, {
        transaction_id: row.id,
      })
    );
  };

  return (
    <TableRow
      hover
      sx={{
        borderBottomStyle: 'solid',
        borderBottomColor: theme.palette.grey[500_24],
        borderBottomWidth: 'thin',
        cursor: 'pointer',
      }}
      onClick={onClick}
    >
      <TableCell align="left" sx={{ maxWidth: 210 }}>
        <OneLineTypography variant="subtitle2">
          {row.resident?.first_name} {row.resident?.last_name}
        </OneLineTypography>
      </TableCell>
      <TableCell>
        <OneLineTypography variant="subtitle2">
          {t(`receiptType.${row.receipt_type.name}`)}
        </OneLineTypography>
      </TableCell>
      <TableCell align="right">
        {paymentProcessor === 'mangopay' ? (
          <Tooltip
            arrow
            placement={'right'}
            title={t('dashboard.table.amount-tooltip', {
              amount: fCurrencyCents(row.amount),
              additionalFeeAmount: fCurrencyCents(row.additional_fee_amount),
            })}
          >
            <Stack
              direction="column"
              sx={{
                gap: 0.5,
                minHeight: 34,
                justifyContent: 'center',
              }}
            >
              <OneLineTypography variant="subtitle2">
                {fCurrencyCents(row.amount)}
              </OneLineTypography>
              {row.additional_fee_amount !== 0 && (
                <OneLineTypography variant="subtitle2" color="text.secondary">
                  {row.additional_fee_amount !== 0
                    ? fCurrencyCents(row.additional_fee_amount)
                    : '--'}
                </OneLineTypography>
              )}
            </Stack>
          </Tooltip>
        ) : (
          <OneLineTypography variant="subtitle2">
            {fCurrencyCents(row.amount)}
          </OneLineTypography>
        )}
      </TableCell>
      <TableCell align="center">
        <ReceiptStatusLabel status={row.receipt_state} />
      </TableCell>
    </TableRow>
  );
};

const tableMaxRows = 5;
